import {useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Master kill-switch for the deep-nesting lazy-load fallback. Flip to `false`
 * to disable the runtime fetch entirely (e.g. once Optimizely Graph's
 * MaxExpansionDepth is raised on the tenant and re-indexing is complete).
 */
const LAZY_LOAD_ENABLED = true

/**
 * Fallback hook for Optimizely Graph's max-expansion-depth cap.
 *
 * GDISizeGuidesBlock sits 2 levels deep inside GDITabsLayoutBlock on the size-guide
 * page. Its Content items (GDITabsLayoutBlock → GDICarouselGridBlock → GDICtaBlock)
 * require 3+ additional levels of Expanded resolution, which exceeds the tenant's
 * MaxExpansionDepth. When this happens, some Content items arrive with
 * `Expanded: {}` — an object with no __typename.
 *
 * This hook detects that condition and re-fetches the entire GDISizeGuidesBlock by
 * its own ContentLink.Id. Querying a single Id as the GraphQL root resets the
 * expansion-depth counter, so the block's Content items come back fully resolved
 * (up to MaxExpansionDepth levels from this new root).
 *
 * GDISizeGuidesBlock is a reusable content item translated via Optimizely
 * language branches (one ContentLink.Id, several Language versions) rather
 * than a per-locale tree node. Without the `locale` argument, Graph returns
 * every language branch for that Id and `items[0]` could pick the wrong one.
 * Passing the active locale via Graph's own `locale: [Locales]` argument
 * resolves to exactly one branch.
 *
 * Returns the freshest available content array: the fetched one when the request
 * has completed, otherwise the original array from the page payload.
 *
 * @param {number|undefined} blockId   - ContentLink.Id of the GDISizeGuidesBlock
 * @param {Array}            contentArea - GDISizeGuidesBlockContent from the page payload
 */

const buildQuery = (fragments) => `
query ($id: Int!, $locale: [Locales]) {
    Content(where: { ContentLink: { Id: { eq: $id } } }, locale: $locale) {
        items { ...IContentFragment }
    }
}
${fragments}
`

/**
 * Returns true when any item in the content area shows the Optimizely Graph
 * MaxExpansionDepth signal. Two patterns:
 *   1. `Expanded: null` or absent  — the most common form: ContentLink.Id
 *      is present (the reference is valid) but Graph refused to expand it.
 *   2. `Expanded: {}` with no __typename — Graph returned a stub object.
 */
const hasDepthCapEntries = (contentArea) => {
    if (!Array.isArray(contentArea)) return false
    return contentArea.some((item) => {
        const contentLink = item?.ContentLink
        if (!contentLink?.Id) return false // No valid link — skip
        const exp = contentLink.Expanded
        // Pattern 1: valid reference but Expanded is null/undefined
        if (exp == null) return true
        // Pattern 2: truthy stub with no __typename
        if (typeof exp === 'object' && !exp.__typename) return true
        return false
    })
}

const useSizeGuidesLazyContent = ({blockId, contentArea}) => {
    const {locale} = useIntl()
    const [fetched, setFetched] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const fetchedIdRef = useRef(null)

    const needsLazyLoad = useMemo(() => hasDepthCapEntries(contentArea), [contentArea])
    // Graph's Locales enum uses underscores (e.g. fr_CA) — the app's locale
    // strings use hyphens (e.g. fr-CA).
    const graphLocale = locale?.replace('-', '_')

    useEffect(() => {
        if (!LAZY_LOAD_ENABLED) return undefined
        if (!needsLazyLoad) return undefined
        if (typeof blockId !== 'number') return undefined
        // Dedupe: fetch each block Id at most once per mounted lifetime.
        if (fetchedIdRef.current === blockId) return undefined

        const {app: appConfig} = getConfig() || {}
        const gateway = appConfig?.OPTIMIZELY_GRAPH_GATEWAY
        const singleKey = appConfig?.OPTIMIZELY_GRAPH_SINGLE_KEY
        if (!gateway || !singleKey) return undefined

        fetchedIdRef.current = blockId
        let aborted = false
        setIsLoading(true)
        ;(async () => {
            try {
                const {fragments} = await import('../../../../../../../optimizely/query')
                const response = await fetch(`${gateway}content/v2?auth=${singleKey}`, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        query: buildQuery(fragments),
                        variables: {id: blockId, locale: graphLocale ? [graphLocale] : undefined}
                    })
                })
                if (!response.ok) return
                const json = await response.json()
                if (aborted) return
                const block = json?.data?.Content?.items?.[0]
                if (!block) return
                setFetched(block.GDISizeGuidesBlockContent)
            } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.warn('[GDISizeGuidesBlock] Lazy content fetch failed:', e)
                }
            } finally {
                if (!aborted) setIsLoading(false)
            }
        })()

        return () => {
            aborted = true
        }
    }, [needsLazyLoad, blockId, graphLocale])

    return useMemo(
        () => ({
            contentArea: fetched ?? contentArea,
            isLoading
        }),
        [fetched, contentArea, isLoading]
    )
}

export default useSizeGuidesLazyContent
